import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useTranslation } from '../../i18n/TranslationContext';
import { useNotification } from '../../hooks/useNotification';
import DashboardService from '../../service';

const CalculatorDestinationsPage = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await DashboardService.getGeDestinationsAll());
    } catch (error) {
      showError(error.message || t('calculatorDestinationsPage.loadError'));
    } finally {
      setLoading(false);
    }
  }, [showError, t]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingSlug(null);
    form.resetFields();
    form.setFieldsValue({ priceUsd: 0, sortOrder: 0, isActive: true });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditingSlug(row.slug);
    form.setFieldsValue({
      slug: row.slug,
      label: row.label,
      priceUsd: Number(row.priceUsd) || 0,
      sortOrder: row.sortOrder ?? 0,
      isActive: row.isActive !== false,
    });
    setModalOpen(true);
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      if (editingSlug) {
        await DashboardService.updateGeDestination(editingSlug, {
          label: values.label,
          priceUsd: Number(values.priceUsd) || 0,
          sortOrder: Number(values.sortOrder) || 0,
          isActive: values.isActive !== false,
        });
        showSuccess(t('common.update'));
      } else {
        await DashboardService.createGeDestination({
          slug: values.slug.trim().toLowerCase(),
          label: values.label.trim(),
          priceUsd: Number(values.priceUsd) || 0,
          sortOrder: Number(values.sortOrder) || 0,
        });
        showSuccess(t('common.create'));
      }
      setModalOpen(false);
      await load();
    } catch (error) {
      if (error.errorFields) return;
      showError(error.message || t('calculatorDestinationsPage.saveError'));
    }
  };

  const disableRow = async (slug) => {
    try {
      await DashboardService.deleteGeDestination(slug);
      showSuccess(t('calculatorDestinationsPage.disabledOk'));
      await load();
    } catch (error) {
      showError(error.message || t('calculatorDestinationsPage.saveError'));
    }
  };

  const confirmPermanentRemove = async () => {
    if (!removeTarget) return;
    try {
      await DashboardService.deleteGeDestination(removeTarget.slug, { permanent: true });
      showSuccess(t('calculatorDestinationsPage.removedOk'));
      setRemoveTarget(null);
      await load();
    } catch (error) {
      showError(error.message || t('calculatorDestinationsPage.saveError'));
    }
  };

  const columns = [
    {
      title: t('calculatorDestinationsPage.colSlug'),
      dataIndex: 'slug',
      key: 'slug',
      render: (value) => <Typography.Text code>{value}</Typography.Text>,
    },
    {
      title: t('calculatorDestinationsPage.colLabel'),
      dataIndex: 'label',
      key: 'label',
    },
    {
      title: t('calculatorDestinationsPage.colPriceUsd'),
      dataIndex: 'priceUsd',
      key: 'priceUsd',
      align: 'right',
      render: (value) => Number(value || 0).toFixed(2),
    },
    {
      title: t('calculatorDestinationsPage.colSort'),
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      align: 'right',
      render: (value) => value ?? 0,
    },
    {
      title: t('common.status'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (value) => (
        <Tag color={value !== false ? 'green' : 'default'}>
          {value !== false ? t('calculatorDestinationsPage.activeLabel') : t('calculatorDestinationsPage.inactive')}
        </Tag>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      align: 'right',
      render: (_, row) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          {row.isActive !== false && (
            <Button type="text" icon={<StopOutlined />} onClick={() => disableRow(row.slug)} />
          )}
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => setRemoveTarget(row)} />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={16} style={{ marginBottom: 24 }}>
        <Flex vertical>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {t('calculatorDestinationsPage.title')}
          </Typography.Title>
          <Typography.Text type="secondary">{t('calculatorDestinationsPage.subtitle')}</Typography.Text>
        </Flex>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} disabled={loading}>
            {t('common.refresh')}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            {t('calculatorDestinationsPage.addDestination')}
          </Button>
        </Space>
      </Flex>

      <Table
        rowKey="slug"
        loading={loading}
        columns={columns}
        dataSource={rows}
        locale={{ emptyText: t('common.noDataFound') }}
      />

      <Modal
        title={editingSlug ? t('common.edit') : t('calculatorDestinationsPage.addDestination')}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={save}
        okText={t('common.save')}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {!editingSlug && (
            <Form.Item
              name="slug"
              label={t('calculatorDestinationsPage.colSlug')}
              extra={t('calculatorDestinationsPage.slugFormatHint')}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          )}
          <Form.Item name="label" label={t('calculatorDestinationsPage.colLabel')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="priceUsd" label={t('calculatorDestinationsPage.colPriceUsd')}>
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="sortOrder" label={t('calculatorDestinationsPage.colSort')}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          {editingSlug && (
            <Form.Item
              name="isActive"
              label={t('calculatorDestinationsPage.activeInCalculator')}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title={t('calculatorDestinationsPage.confirmRemoveTitle')}
        open={!!removeTarget}
        onCancel={() => setRemoveTarget(null)}
        onOk={confirmPermanentRemove}
        okText={t('calculatorDestinationsPage.confirmRemoveAction')}
        okButtonProps={{ danger: true }}
      >
        <Typography.Text>
          {t('calculatorDestinationsPage.confirmRemoveBody').replace(/\{slug\}/g, removeTarget?.slug ?? '')}
        </Typography.Text>
      </Modal>
    </div>
  );
};

export default CalculatorDestinationsPage;
